import { useCallback, useMemo } from "react";

import {
  faEdit,
  faFlask,
  faWavePulse,
} from "@fortawesome/pro-regular-svg-icons";
import { AnimatePresence, m } from "motion/react";
import { useLocalStorage, useSessionStorage } from "usehooks-ts";

import type { ChartExploreContext } from "src/analytics/copilot/chartExploreContext";
import { InsightsCopilotButton } from "src/analytics/copilot/InsightsCopilotButton";
import { useChartExplore } from "src/analytics/copilot/useChartExplore";
import { DashboardEditActions } from "src/analytics/dashboard/DashboardEditActions";
import { DashboardFormModal } from "src/analytics/dashboard/DashboardFormModal";
import { DashboardGrid } from "src/analytics/dashboard/DashboardGrid";
import { DashboardGridSkeleton } from "src/analytics/dashboard/DashboardGridSkeleton";
import { DashboardLastEditedInfo } from "src/analytics/dashboard/DashboardLastEditedInfo";
import { DashboardMetricsProvider } from "src/analytics/dashboard/DashboardMetricsContext";
import { DashboardRefreshButton } from "src/analytics/dashboard/DashboardRefreshButton";
import { DashboardTabs } from "src/analytics/dashboard/DashboardTabs";
import { defaultDashboardWindow } from "src/analytics/dashboard/dashboardFilterParams";
import { JustPinnedProvider } from "src/analytics/dashboard/JustPinnedContext";
import { useDashboard } from "src/analytics/dashboard/useDashboard";
import { useDashboardLayoutEditing } from "src/analytics/dashboard/useDashboardLayoutEditing";
import {
  useDeleteDashboard,
  useListDashboardsQuery,
} from "src/api/analytics-dashboard/dashboard";
import { isPerformanceDashboard } from "src/api/analytics-dashboard/fixedDashboards";
import { useFlow } from "src/api/queries";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { TextConfirmationModal } from "src/base-components/TextConfirmationModal";
import { Realm } from "src/clients/analytics-dashboard";
import { useModal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { CopilotSidebar } from "src/flow/analytics/CopilotSidebar";
import { useFlowChartRequest } from "src/flow/analytics/m1/suggestedQuestions/useFlowChartRequest";
import { useFlowSuggestedQuestions } from "src/flow/analytics/m1/suggestedQuestions/useFlowSuggestedQuestions";
import { useLiveAnalyticsAccess } from "src/flow/analytics/useLiveAnalyticsAccess";
import { useSpecialDomainLabels } from "src/flow/analytics/useSpecialDomainLabels";
import { useTrackCopilotOpened } from "src/flow/analytics/useTrackCopilotOpened";
import { FlowPageLayout } from "src/flow/FlowPageLayout";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useEnvironment } from "src/hooks/useEnvironment";
import { useIsFeatureFlagEnabled } from "src/hooks/useIsFeatureFlagEnabled";
import { SubHeaderDatePicker } from "src/layout/SubHeader";
import { FEATURE_FLAGS } from "src/router/featureFlags";
import { FlowPageParams } from "src/router/urls";
import { type StrictDateRange, useTimeWindow } from "src/utils/timeWindow";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const FlowAnalyticsContent: React.FC = () => {
  const { flow_id } = useParamsDecode<FlowPageParams>();
  const { data: flow } = useFlow(flow_id, {
    refetchOnMount: true,
  });
  const [environment, setEnvironment] = useEnvironment();
  const labelDictionaries = useSpecialDomainLabels(flow_id, flow?.name ?? "");
  const [isCopilotOpen, setIsCopilotOpen] = useSessionStorage(
    "flowAnalyticsCopilotOpen",
    false,
  );
  const openCopilot = useCallback(
    () => setIsCopilotOpen(true),
    [setIsCopilotOpen],
  );
  const { pendingChartContext, exploreWithCopilot, clearPendingContext } =
    useChartExplore(openCopilot);

  const trackCopilotOpened = useTrackCopilotOpened();
  const handleOpenCopilotFromHeader = useCallback(() => {
    trackCopilotOpened("header_button");
    openCopilot();
  }, [trackCopilotOpened, openCopilot]);
  const handleOpenCopilotFromDashboard = useCallback(() => {
    trackCopilotOpened("dashboard_empty_state");
    openCopilot();
  }, [trackCopilotOpened, openCopilot]);
  const handleExploreWithCopilot = useCallback(
    (context: ChartExploreContext) => {
      trackCopilotOpened("chart_explore");
      exploreWithCopilot(context);
    },
    [trackCopilotOpened, exploreWithCopilot],
  );

  const defaultWindow = useMemo<StrictDateRange>(
    () => defaultDashboardWindow(),
    [],
  );
  const { dateRangePickerValue, onDateRangePickerChange, timeWindow } =
    useTimeWindow<StrictDateRange>(defaultWindow, "flowAnalyticsTimeWindow");

  const { canAccess: hasLiveAnalyticsAccess } = useLiveAnalyticsAccess();
  const isSuggestedQuestionsEnabled = useIsFeatureFlagEnabled(
    FEATURE_FLAGS.liveAnalyticsSuggestedQuestions,
  );
  const {
    questions: suggestedQuestions,
    isGenerating: isGeneratingSuggestions,
  } = useFlowSuggestedQuestions({
    flowId: flow_id,
    flowName: flow?.name ?? "",
    enabled:
      isSuggestedQuestionsEnabled &&
      hasLiveAnalyticsAccess &&
      flow?.name !== undefined,
  });

  const { pendingMessage, requestCharts, clearRequest } =
    useFlowChartRequest(flow_id);
  const handleBuildCharts = useCallback(
    (questions: string[]) => {
      requestCharts(questions);
      trackCopilotOpened("dashboard_empty_state");
      openCopilot();
    },
    [requestCharts, trackCopilotOpened, openCopilot],
  );

  const { data: dashboards } = useListDashboardsQuery(Realm.FLOWS, flow_id);
  const [activeDashboardId, setActiveDashboardId] = useLocalStorage<
    string | undefined
  >(`flowAnalyticsActiveDashboard:${flow_id}`, undefined);
  const resolvedDashboardId =
    activeDashboardId && dashboards?.some((d) => d.id === activeDashboardId)
      ? activeDashboardId
      : dashboards?.[0]?.id;
  const isReadOnlyDashboard = isPerformanceDashboard(
    Realm.FLOWS,
    resolvedDashboardId,
  );

  const editing = useDashboardLayoutEditing(
    Realm.FLOWS,
    flow_id,
    resolvedDashboardId,
  );
  const { dashboard } = useDashboard(Realm.FLOWS, flow_id, resolvedDashboardId);

  // Until the list resolves, the hooks above fall back to the default
  // dashboard, whose pins are not the stored selection's.
  const pins = resolvedDashboardId ? Object.values(dashboard.pins) : [];

  // The dashboard service gates flow dashboard writes on the same permission
  // this capability tracks, so it governs flow tabs too despite the name.
  const { workspaceInsights } = useCapabilities();

  const {
    isOpen: isCreateOpen,
    openModal: openCreateModal,
    closeModal: closeCreateModal,
    afterLeave: createAfterLeave,
  } = useModal();

  const {
    isOpen: isEditOpen,
    data: editingDashboardId,
    openModal: openEditModal,
    closeModal: closeEditModal,
    afterLeave: editAfterLeave,
  } = useModal<string>();

  const {
    isOpen: isDeleteOpen,
    data: deletingDashboard,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal,
    afterLeave: deleteAfterLeave,
  } = useModal<{ id: string; name: string }>();

  const deleteDashboard = useDeleteDashboard(Realm.FLOWS, flow_id);
  const deletingDashboardName = deletingDashboard?.name ?? "Untitled Dashboard";

  const handleDeleteConfirm = async () => {
    if (!deletingDashboard) return;
    try {
      await deleteDashboard.mutateAsync(deletingDashboard.id);
      if (activeDashboardId === deletingDashboard.id) {
        setActiveDashboardId(undefined);
      }
      toastActions.success({ title: "Successfully deleted dashboard" });
    } catch {
      toastActions.failure({ title: "Error deleting dashboard" });
    }
    closeDeleteModal();
  };

  return (
    <JustPinnedProvider key={`${Realm.FLOWS}:${flow_id}`}>
      <DashboardMetricsProvider pins={pins} timeWindow={timeWindow}>
        <FlowPageLayout
          flow={flow}
          headerActions={
            <>
              <div className="pr-3">
                <DashboardLastEditedInfo
                  dashboardId={resolvedDashboardId}
                  realm={Realm.FLOWS}
                  realmId={flow_id}
                />
              </div>
              <AnimatePresence initial={false}>
                {!isCopilotOpen && !editing.isEditing && (
                  <m.div
                    key="ask-copilot"
                    className="pr-3"
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    initial={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                  >
                    <InsightsCopilotButton
                      onClick={handleOpenCopilotFromHeader}
                    />
                  </m.div>
                )}
              </AnimatePresence>
            </>
          }
          rightPanel={
            flow && (
              <AnimatePresence initial={false} mode="wait">
                {isCopilotOpen && !editing.isEditing && (
                  <CopilotSidebar
                    key="copilot-sidebar"
                    dashboardId={resolvedDashboardId}
                    flowId={flow_id}
                    flowName={flow.name}
                    isLayoutEditing={editing.isEditing}
                    onClearPendingInitialMessage={clearRequest}
                    onClearPendingStructuredInput={clearPendingContext}
                    onClose={() => setIsCopilotOpen(false)}
                    pendingInitialMessage={pendingMessage}
                    pendingStructuredInput={pendingChartContext}
                    suggestedQuestions={suggestedQuestions}
                  />
                )}
              </AnimatePresence>
            )
          }
        >
          <div className="sticky -top-5 z-10 -mx-5 -mt-5 mb-5">
            <DashboardTabs
              activeDashboardId={resolvedDashboardId ?? ""}
              canCreate={workspaceInsights.canEdit}
              realm={Realm.FLOWS}
              realmId={flow_id}
              onCreateRequest={() => openCreateModal()}
              onDashboardChange={setActiveDashboardId}
              onDeleteRequest={
                workspaceInsights.canEdit
                  ? (dashboardId) =>
                      openDeleteModal({
                        id: dashboardId,
                        name:
                          dashboards?.find((d) => d.id === dashboardId)?.name ??
                          "Untitled Dashboard",
                      })
                  : undefined
              }
              onEditRequest={
                workspaceInsights.canEdit ? openEditModal : undefined
              }
            >
              {editing.isEditing ? (
                <DashboardEditActions
                  isSaving={editing.isSaving}
                  onCancel={editing.cancel}
                  onSave={editing.save}
                />
              ) : (
                <>
                  <SubHeaderDatePicker
                    placeholder="Last 30 days"
                    value={dateRangePickerValue}
                    onChange={onDateRangePickerChange}
                  />
                  <DashboardRefreshButton />
                  <span className="h-full w-12 border-gray-200 border-l p-3.5">
                    <EllipsisOptionsDropdown
                      dataLoc="flow-analytics-page-menu"
                      elements={[
                        ...(isReadOnlyDashboard
                          ? []
                          : [
                              {
                                key: "Organize dashboard",
                                icon: faEdit,
                                action: editing.start,
                                disabled: !editing.canEdit,
                              },
                            ]),
                        {
                          key: `Switch to ${environment === "sandbox" ? "Live" : "Sandbox"}`,
                          icon:
                            environment === "sandbox" ? faWavePulse : faFlask,
                          action: () =>
                            setEnvironment(
                              environment === "sandbox" ? "live" : "sandbox",
                            ),
                        },
                      ]}
                      iconSize="sm"
                      placement="bottom start"
                    />
                  </span>
                </>
              )}
            </DashboardTabs>
          </div>
          {resolvedDashboardId ? (
            <DashboardGrid
              dashboardId={resolvedDashboardId}
              editLayout={editing.layout}
              isEditing={editing.isEditing}
              isSaving={editing.isSaving}
              labelDictionaries={labelDictionaries}
              onEditLayoutChange={editing.onLayoutChange}
              isGeneratingSuggestions={isGeneratingSuggestions}
              onBuildCharts={handleBuildCharts}
              onExploreWithCopilot={handleExploreWithCopilot}
              onOpenCopilot={handleOpenCopilotFromDashboard}
              realm={Realm.FLOWS}
              realmId={flow_id}
              suggestedQuestions={suggestedQuestions}
            />
          ) : (
            <DashboardGridSkeleton />
          )}
          <DashboardFormModal
            afterLeave={createAfterLeave}
            open={isCreateOpen}
            realm={Realm.FLOWS}
            realmId={flow_id}
            onClose={closeCreateModal}
            onCreated={setActiveDashboardId}
          />
          <DashboardFormModal
            afterLeave={editAfterLeave}
            dashboardId={editingDashboardId}
            open={isEditOpen}
            realm={Realm.FLOWS}
            realmId={flow_id}
            onClose={closeEditModal}
          />
          <TextConfirmationModal
            afterLeave={deleteAfterLeave}
            challengeText={deletingDashboardName}
            confirmButtonText="Delete"
            description={`This permanently deletes "${deletingDashboardName}" and its charts. Type the dashboard name to confirm.`}
            label="Dashboard name"
            open={isDeleteOpen}
            title="Delete this dashboard?"
            onClose={closeDeleteModal}
            onConfirm={handleDeleteConfirm}
          />
        </FlowPageLayout>
      </DashboardMetricsProvider>
    </JustPinnedProvider>
  );
};
